import { UploadSource } from '../../upload/models/upload-session.model';
import { UploadButtonService } from '../../upload/upload-button/upload-button.service';
import { UploadConfigService } from '../../upload/upload-config.service';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { DocumentService } from '../services/document.service';
import { GuidGenerator } from './guid-generator.utility';

export class DragAndDrop {

    private static readonly ScrollContentId = 'officeHome__scroll-content';
    private static readonly DragAndDropStyle = 'drag-and-drop--outline';

    private readonly enabled: boolean;

    private timeoutTracker : ReturnType<typeof setTimeout>;

    constructor(
        private readonly documentService: DocumentService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly uploadButtonService: UploadButtonService,
        private readonly accessTokenService: AccessTokenService,
        private readonly isConsumer: boolean
    ) {
        this.enabled = !!this.uploadConfigService.config && this.uploadConfigService.config.dragAndDropEnabled;
    }

    public onDragOver(event: DragEvent) {
        if (this.enabled &&
            this.isLocalFile(event.dataTransfer.types)) {
            // Set the dropEffect to copy
            event.dataTransfer.dropEffect = 'copy';

            // Outline does not show properly on IE 11 due to lack of support for outline-offset
            // Consider showing image overlay in the future
            this.showOutline();
            clearTimeout(this.timeoutTracker);

            this.timeoutTracker = setTimeout(() => {
                this.hideOutline();
            }, 300);
        } else {
            event.dataTransfer.dropEffect = 'none';
        }
    }

    public onDrop(event: DragEvent) {
        if (this.enabled &&
            this.isLocalFile(event.dataTransfer.types) &&
            event.dataTransfer.files.length > 0) {

            // For some reason, accessing the 'event.dataTransfer.files' object from within the promise handlers below doesn't work. The 'files' list will
            // be empty. Therefore, save the files list in a variable outside of the promises.
            const files = event.dataTransfer.files;
            const tokenType = AccessTokenConsumer.Graph;
            const interactive = this.isConsumer || this.uploadConfigService.config.capPopupEnabled;
            this.accessTokenService.getAccessToken(tokenType, false, null, interactive, { category: ErrorDialogErrorCategory.Upload })
                .then(() => {
                    this.uploadFiles(files);
                });

            clearTimeout(this.timeoutTracker);
            this.hideOutline();
        }
    }

    private uploadFiles(files: FileList) {
        if (files.length === 1) {
            this.uploadButtonService.uploadFile(files[0], null, GuidGenerator.createGuid(), 'upload-drop', UploadSource.DragAndDrop);
        } else {
            this.uploadButtonService.uploadFiles(files, GuidGenerator.createGuid(), 'upload-drop', UploadSource.DragAndDrop);
        }
    }

    private showOutline() {
        this.documentService.addCssClassToElement(this.documentService.nativeDocument.getElementById(DragAndDrop.ScrollContentId), DragAndDrop.DragAndDropStyle);
    }

    private hideOutline() {
        this.documentService.removeCssClassFromElement(this.documentService.nativeDocument.getElementById(DragAndDrop.ScrollContentId), DragAndDrop.DragAndDropStyle);
    }

    private isLocalFile(types: ReadonlyArray<string>): boolean {
        for (let i = 0; i < types.length; i++) {
            const type = types[i];
            // Only accept known local file types
            // Firefox includes 'application/x-moz-file' for local files
            // Edge includes 'Files' in addition to other types for page elements which we need to filter out
            if (type !== 'Files' && type !== 'application/x-moz-file') {
                return false;
            }
        }
        return true;
    }
}
